<template>
  <Table :columns="columns" :data="rows" :is-row-clickable="statsClickable" @row-click="handleRowClick">
    <template #rank="{ row }">
      <div
        v-if="row.rank"
        class="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
        :class="getMedalClass(row.rank)"
      >
        {{ row.rank }}
      </div>
    </template>
    <template #name="{ row }">
      <div class="font-semibold text-gray-900">{{ row.name }}</div>
    </template>
    <template #sales_combined="{ row }">
      <div class="text-right">
        <span v-if="row.salesCount != null && row.salesCountTarget != null && row.salesCountTarget != 0">
          <span class="font-bold">{{ t("sales_count", row.salesCount) }}</span>
          <em v-if="row.leadsProcessingCount > 0" class="text-xs text-gray-500 ml-1">
            {{ t("sepa_pending", { count: row.leadsProcessingCount }) }}
          </em>
          <span class="text-sm text-gray-500 ml-1">/ {{ row.salesCountTarget }}</span>
          <span class="ml-1">{{ getTargetBadge(row.salesCountResultTarget) }}</span>
        </span>
        <span v-else-if="row.salesCount != null">
          <span class="font-bold">{{ t("sales_count", row.salesCount) }}</span>
          <em v-if="row.leadsProcessingCount > 0" class="text-xs text-gray-500 ml-1">
            {{ t("sepa_pending", { count: row.leadsProcessingCount }) }}
          </em>
        </span>
        <span v-else class="text-gray-400">—</span>
      </div>
    </template>
    <template #cancellation_rate="{ row }">
      <div class="text-right font-thin text-gray-500">
        {{ formatPercentage(row.cancellationRate) ?? "—" }}
      </div>
    </template>
    <template #projection_combined="{ row }">
      <div class="text-right">
        <span v-if="row.salesCountProjection != null">
          <span class="font-bold">{{ t("sales_count", row.salesCountProjection) }}</span>
          <span
            v-if="row.salesCountProjectionVariation != null"
            :class="`ml-1 ${getVariationColorClass(row.salesCountProjectionVariation)}`"
          >
            • {{ getVariationArrow(row.salesCountProjectionVariation) }}
            {{ formatVariationWithSign(row.salesCountProjectionVariation) }}% vs {{ formattedPreviousMonth }}
          </span>
          <span v-else class="ml-1 text-gray-400">• — vs {{ formattedPreviousMonth }}</span>
        </span>
        <span v-else class="text-gray-400">—</span>
      </div>
    </template>
  </Table>
</template>

<script>
import { Table, formatPercentage, computeCompetitionRanking } from "@estaly/ui";
import { format, subMonths, parse } from "date-fns";
import { fr } from "date-fns/locale";
import { useI18n } from "vue-i18n";

export default {
  components: {
    Table,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    stats: {
      type: Array,
      required: true,
    },
    total: {
      type: Object,
      required: true,
    },
    currentMonth: {
      type: String,
      required: true,
    },
    statsClickable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    columns() {
      return [
        {
          label: null,
          field: "rank",
          slot: "rank",
          widthClass: "w-12",
          headerGroup: "",
        },
        {
          field: "name",
          label: null,
          slot: "name",
          headerGroup: "",
        },
        {
          field: "sales_combined",
          label: this.t("sales_column"),
          slot: "sales_combined",
          headerGroup: "",
          numeric: true,
        },
        {
          field: "cancellation_rate",
          label: this.t("cancellation_column"),
          slot: "cancellation_rate",
          headerGroup: "",
          numeric: true,
        },
        {
          field: "projection_combined",
          label: this.t("projection_column", { month: this.formattedShortMonth }),
          slot: "projection_combined",
          headerGroup: "",
          numeric: true,
        },
      ];
    },

    statsRows() {
      return this.stats || [];
    },

    totalRow() {
      return [this.total];
    },

    rows() {
      const rowsWithIndex = this.statsRows.map((row, originalIndex) => ({
        ...row,
        _originalIndex: originalIndex,
      }));

      const sortedBySales = [...rowsWithIndex].sort((a, b) => {
        const salesA = a.salesCount || 0;
        const salesB = b.salesCount || 0;
        return salesB - salesA;
      });

      const ranked = computeCompetitionRanking(sortedBySales, (item) => item.salesCount || 0);
      const rankMap = new Map(ranked.map((row) => [row._originalIndex, row.rank]));

      const statsWithRank = this.statsRows.map((row, index) => {
        const rank = rankMap.get(index) || null;

        return {
          ...row,
          rank: rank,
          clickable: this.statsClickable,
          body: true,
          rowClass: "",
        };
      });

      return [
        ...this.totalRow.map((row) => ({
          ...row,
          rank: null,
          clickable: false,
          rowClass: "bg-gray-100",
        })),
        ...statsWithRank,
      ];
    },

    formattedShortMonth() {
      if (!this.currentMonth) return "";
      return this.formatShortMonth(this.currentMonth);
    },

    formattedPreviousMonth() {
      if (!this.currentMonth) return "M-1";
      return this.formatShortMonth(this.currentMonth, true);
    },
  },

  methods: {
    handleRowClick(row) {
      if (!row.clickable) return;
      this.$emit("rowClicked", row);
    },

    getMedalClass(ranking) {
      if (ranking === 0 || ranking == null) return "";

      const classes = [
        "bg-linear-to-r/srgb from-yellow-400 to-yellow-500 text-white",
        "bg-linear-to-r/srgb from-gray-300 to-gray-400 text-white",
        "bg-linear-to-r/srgb from-orange-400 to-orange-800 text-white",
      ];
      return classes[ranking - 1] || "bg-gray-100 text-gray-600";
    },

    formatVariation(variation) {
      return variation != null ? variation : "+ ∞";
    },
    formatPercentage,
    formatShortMonth(monthString, previous = false) {
      if (!monthString) return previous ? "M-1" : "";

      try {
        const date = parse(monthString, "MMMM yyyy", new Date(), { locale: fr });
        if (isNaN(date.getTime())) return previous ? "M-1" : monthString;

        const targetDate = previous ? subMonths(date, 1) : date;
        return format(targetDate, "MMM yy", { locale: fr });
      } catch (error) {
        return previous ? "M-1" : monthString;
      }
    },

    getVariationArrow(variation) {
      if (variation == null) return "";
      const formatted = this.formatVariation(variation);
      if (formatted > 0 || formatted === "+ ∞") {
        return "▲";
      }
      if (formatted < 0) {
        return "▼";
      }
      return "";
    },

    getVariationColorClass(variation) {
      if (variation == null) return "text-gray-500";
      const formatted = this.formatVariation(variation);
      if (formatted > 0 || formatted === "+ ∞") {
        return "text-green-600";
      }
      if (formatted < 0) {
        return "text-red-600";
      }
      return "text-gray-500";
    },

    formatVariationWithSign(variation) {
      if (variation == null) return this.formatVariation(variation);
      const formatted = this.formatVariation(variation);
      if (formatted > 0 && typeof formatted === "number") {
        return `+${formatted}`;
      }
      return formatted;
    },

    getTargetBadge(percentage) {
      if (percentage == null) return "";
      if (percentage >= 80) {
        return "🟢";
      }
      if (percentage >= 60) {
        return "🟠";
      }
      return "🔴";
    },
  },
};
</script>

<i18n src="./CommonAnalyticsTable.i18n.json"></i18n>
